import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  ArrowLeft,
  BadgeCheck,
  KeyRound,
  Lock,
  LockOpen,
  Search,
  ShieldCheck,
  ShieldMinus,
  Users,
} from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type { FichaUsuario, PaginaUsuarios } from '../../api/tipos';
import {
  Abas,
  Aviso,
  BarraFerramentas,
  Botao,
  Campo,
  Cartao,
  Celula,
  Crescer,
  Definicoes,
  Esqueleto,
  Etiqueta,
  Paginacao,
  Tabela,
  Td,
  Th,
  useConfirmar,
  useNotificar,
  Vazio,
} from '../../componentes/ui';
import { Colunas, Pagina, TituloPagina } from '../../componentes/Pagina';
import {
  dataHora,
  numero,
  plural,
  reais,
  tempoRelativo,
} from '../../util/formato';
import { formatarDocumento, mascaraTelefone } from '../../util/mascaras';
import css from './UsuariosAdmin.module.css';

/** Espera antes de buscar — evita uma consulta por tecla digitada. */
const ATRASO_BUSCA_MS = 350;

const FILTROS = [
  { valor: 'todos', rotulo: 'Todos' },
  { valor: 'naoVerificados', rotulo: 'Não verificados' },
  { valor: 'inativos', rotulo: 'Bloqueados' },
] as const;

type Filtro = (typeof FILTROS)[number]['valor'];

/** "AGUARDANDO_PAGAMENTO" → "Aguardando pagamento": enum do banco em sentença. */
/** Valores de enum da API com acento; o resto cai na regra genérica. */
const ROTULOS_ENUM: Record<string, string> = {
  CONCLUIDO: 'Concluído',
  CONCLUIDA: 'Concluída',
  RESERVA: 'Reserva',
  ESTORNO: 'Estorno',
  BONUS: 'Bônus',
  CONSUMO: 'Consumo',
  RECARGA: 'Recarga',
  AJUSTE: 'Ajuste',
  ANALISE: 'Análise',
};

function legivel(valor: string): string {
  if (ROTULOS_ENUM[valor]) return ROTULOS_ENUM[valor];
  const s = valor.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Etiquetas de situação da conta — as mesmas na lista e na ficha. */
function EtiquetasConta({
  u,
  mostrarAtiva,
}: {
  u: {
    ativo: boolean;
    senhaProvisoria?: boolean;
    emailVerificadoEm?: string | null;
    role: string;
  };
  mostrarAtiva?: boolean;
}) {
  return (
    <div className={css.etiquetas}>
      {!u.ativo && <Etiqueta tom="erro">Bloqueada</Etiqueta>}
      {u.senhaProvisoria && <Etiqueta tom="alerta">Senha provisória</Etiqueta>}
      {!u.emailVerificadoEm && <Etiqueta tom="alerta">Sem verificar</Etiqueta>}
      {u.role === 'ADMIN' && <Etiqueta tom="info">Admin</Etiqueta>}
      {mostrarAtiva &&
        u.ativo &&
        u.emailVerificadoEm &&
        !u.senhaProvisoria &&
        u.role === 'USER' && <Etiqueta tom="ok">Ativa</Etiqueta>}
    </div>
  );
}

export function UsuariosAdmin() {
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [dados, setDados] = useState<PaginaUsuarios | null>(null);
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const q = new URLSearchParams({
      pagina: String(pagina),
      porPagina: '25',
    });
    if (busca.trim()) q.set('busca', busca.trim());
    if (filtro === 'inativos') q.set('apenasInativos', 'true');
    if (filtro === 'naoVerificados') q.set('apenasNaoVerificados', 'true');

    try {
      setDados(await api<PaginaUsuarios>(`/admin/usuarios?${q.toString()}`));
      setErro(null);
    } catch (err) {
      setErro(
        err instanceof ErroApi ? err.message : 'Falha ao listar usuários.',
      );
    }
  }, [busca, pagina, filtro]);

  useEffect(() => {
    const t = window.setTimeout(() => void carregar(), ATRASO_BUSCA_MS);
    return () => clearTimeout(t);
  }, [carregar]);

  if (selecionado) {
    return (
      <Pagina>
        <FichaDoUsuario
          id={selecionado}
          aoFechar={() => {
            setSelecionado(null);
            void carregar();
          }}
        />
      </Pagina>
    );
  }

  return (
    <Pagina largura="larga">
      <TituloPagina
        titulo="Clientes"
        descricao="Quem usa o sistema, quanto tem em conta e o que já fez. Clique numa linha para abrir a ficha."
      />

      {erro && <Aviso>{erro}</Aviso>}

      <Cartao padding={0}>
        <BarraFerramentas>
          <Crescer>
            <Campo
              rotulo="Buscar clientes"
              rotuloOculto
              type="search"
              icone={<Search />}
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
                setPagina(1);
              }}
              placeholder="Buscar por nome, e-mail ou CPF/CNPJ"
            />
          </Crescer>
          <Abas
            rotulo="Filtrar contas"
            opcoes={FILTROS}
            atual={filtro}
            aoMudar={(v) => {
              setFiltro(v);
              setPagina(1);
            }}
          />
        </BarraFerramentas>

        {!dados ? (
          <div className={css.carregando}>
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
          </div>
        ) : dados.itens.length === 0 ? (
          <Vazio
            icone={<Users />}
            titulo="Nenhuma conta encontrada"
            descricao="Ajuste a busca ou o filtro."
          />
        ) : (
          <>
            <Tabela rotulo="Clientes">
              <thead>
                <tr>
                  <Th>Conta</Th>
                  <Th>Documento</Th>
                  <Th alinhar="direita">Saldo</Th>
                  <Th>Uso</Th>
                  <Th>Último acesso</Th>
                  <Th>Situação</Th>
                </tr>
              </thead>
              <tbody>
                {dados.itens.map((u) => (
                  <tr
                    key={u.id}
                    className={css.linha}
                    onClick={() => setSelecionado(u.id)}
                  >
                    <Td>
                      {/* Botão de verdade no nome: a linha inteira é clicável
                          com o mouse, mas o teclado precisa de um alvo focável. */}
                      <button
                        type="button"
                        className={css.nome}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelecionado(u.id);
                        }}
                      >
                        {u.nome}
                      </button>
                      <span className={css.secundaria}>{u.email}</span>
                    </Td>
                    <Td>
                      <Celula
                        principal={
                          <span className={css.mono}>
                            {formatarDocumento(u.documento)}
                          </span>
                        }
                        secundaria={`${u.cidade}/${u.uf}`}
                      />
                    </Td>
                    <Td alinhar="direita">
                      <Celula
                        principal={
                          <span className="num">
                            {reais(u.carteira?.saldoCentavos ?? 0)}
                          </span>
                        }
                        secundaria={
                          (u.carteira?.reservadoCentavos ?? 0) > 0 ? (
                            <span className={css.reservado}>
                              {reais(u.carteira!.reservadoCentavos)} reservado
                            </span>
                          ) : undefined
                        }
                      />
                    </Td>
                    <Td>
                      <span className={css.apoio}>
                        {[
                          u.uso.buscas > 0 &&
                            `${plural(u.uso.buscas, 'busca')} · ${plural(u.uso.xmlsEntregues, 'XML', 'XMLs')}`,
                          u.uso.consumidoCentavos > 0 &&
                            `${reais(u.uso.consumidoCentavos)} usados`,
                        ]
                          .filter(Boolean)
                          .join(' · ') || 'Nada usado ainda'}
                      </span>
                      <span className={css.secundaria}>
                        {numero(u._count.analisesSped)} SPED ·{' '}
                        {plural(u._count.recargas, 'recarga')}
                        {u._count.apiKeys > 0 &&
                          ` · ${plural(u._count.apiKeys, 'chave de API', 'chaves de API')}`}
                      </span>
                    </Td>
                    <Td>
                      <span className={css.apoio}>
                        {u.ultimoAcessoEm
                          ? tempoRelativo(u.ultimoAcessoEm)
                          : 'Nunca entrou'}
                      </span>
                      <span className={css.secundaria}>
                        Cadastro em {dataHora(u.createdAt)}
                      </span>
                    </Td>
                    <Td>
                      <EtiquetasConta u={u} mostrarAtiva />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Tabela>
            <Paginacao
              total={dados.total}
              pagina={dados.pagina}
              porPagina={dados.porPagina}
              aoMudar={setPagina}
              rotulo={dados.total === 1 ? 'conta' : 'contas'}
            />
          </>
        )}
      </Cartao>
    </Pagina>
  );
}

/* ── Ficha do usuário ───────────────────────────────────────────────────── */

function FichaDoUsuario({
  id,
  aoFechar,
}: {
  id: string;
  aoFechar: () => void;
}) {
  const confirmar = useConfirmar();
  const notificar = useNotificar();
  const [ficha, setFicha] = useState<FichaUsuario | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [valorCredito, setValorCredito] = useState('');
  const [motivo, setMotivo] = useState('');
  const [creditando, setCreditando] = useState(false);
  const [senhaGerada, setSenhaGerada] = useState<string | null>(null);
  const [resetando, setResetando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setFicha(await api<FichaUsuario>(`/admin/usuarios/${id}`));
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Falha ao abrir a conta.');
    }
  }, [id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function alterar(
    mudanca: Record<string, unknown>,
    confirmacao?: {
      titulo: string;
      mensagem: string;
      confirmar: string;
      perigo?: boolean;
    },
  ) {
    if (
      confirmacao &&
      !(await confirmar({ ...confirmacao, cancelar: 'Voltar' }))
    )
      return;
    setErro(null);
    try {
      setFicha(
        await api<FichaUsuario>(`/admin/usuarios/${id}`, {
          metodo: 'PATCH',
          corpo: mudanca,
        }),
      );
      notificar('Alteração aplicada.');
    } catch (err) {
      setErro(
        err instanceof ErroApi ? err.message : 'Não foi possível alterar.',
      );
    }
  }

  async function creditar(e: FormEvent) {
    e.preventDefault();
    const centavos = Number(valorCredito.replace(/\D/g, ''));
    if (centavos <= 0 || !motivo.trim()) return;

    setErro(null);
    setCreditando(true);
    try {
      await api(`/admin/usuarios/${id}/creditar`, {
        metodo: 'POST',
        corpo: { valorCentavos: centavos, descricao: motivo.trim() },
      });
      setValorCredito('');
      setMotivo('');
      notificar(`${reais(centavos)} creditados.`);
      await carregar();
    } catch (err) {
      setErro(
        err instanceof ErroApi ? err.message : 'Não foi possível creditar.',
      );
    } finally {
      setCreditando(false);
    }
  }

  async function resetarSenha(email: string) {
    const confirmado = await confirmar({
      titulo: 'Definir uma senha provisória?',
      mensagem:
        `A conta ${email} recebe uma senha provisória. ` +
        'As sessões abertas caem na hora e a pessoa só consegue entrar de novo ' +
        'com a senha nova — que ela será obrigada a trocar no primeiro acesso.',
      confirmar: 'Redefinir senha',
      cancelar: 'Voltar',
      perigo: true,
    });
    if (!confirmado) return;

    setErro(null);
    setSenhaGerada(null);
    setResetando(true);
    try {
      const r = await api<{ senha: string; email: string }>(
        `/admin/usuarios/${id}/senha`,
        { metodo: 'POST', corpo: {} },
      );
      setSenhaGerada(r.senha);
      await carregar();
    } catch (err) {
      setErro(
        err instanceof ErroApi ? err.message : 'Não foi possível redefinir.',
      );
    } finally {
      setResetando(false);
    }
  }

  const voltar = (
    <div>
      <Botao
        aparencia="discreto"
        pequeno
        icone={<ArrowLeft />}
        onClick={aoFechar}
      >
        Clientes
      </Botao>
    </div>
  );

  if (!ficha) {
    return (
      <>
        {voltar}
        {erro ? (
          <Aviso>{erro}</Aviso>
        ) : (
          <div className={css.carregandoFicha}>
            <Esqueleto altura={28} largura="40%" />
            <Esqueleto altura={220} />
          </div>
        )}
      </>
    );
  }

  const u = ficha.user;

  return (
    <>
      <div className={css.cabecalhoFicha}>
        {voltar}
        <TituloPagina
          titulo={u.nome}
          descricao={u.email}
          acoes={<EtiquetasConta u={u} />}
        />
      </div>

      {erro && <Aviso>{erro}</Aviso>}

      {senhaGerada && (
        <Aviso tom="alerta" titulo="Senha provisória gerada">
          <div className={css.senhaBloco}>
            <span>
              Senha provisória de <strong>{u.email}</strong>. Ela aparece{' '}
              <strong>uma única vez</strong> — depois de fechar esta tela não há
              como recuperá-la, só gerar outra. Passe por um canal seguro:
            </span>
            <code className={css.senha}>{senhaGerada}</code>
            <span className={css.senhaNota}>
              No primeiro acesso o sistema exige a troca antes de liberar
              qualquer tela.
            </span>
          </div>
        </Aviso>
      )}

      <Colunas minimo={360} alinhar="start">
        <Cartao titulo="Cadastro">
          <Definicoes
            itens={[
              ['E-mail', u.email],
              [
                u.tipoPessoa === 'JURIDICA' ? 'CNPJ' : 'CPF',
                <span className={css.mono}>
                  {formatarDocumento(u.documento)}
                </span>,
              ],
              ...(u.telefone
                ? ([['Telefone', mascaraTelefone(u.telefone)]] as const)
                : []),
              [
                'Endereço',
                `${u.logradouro}, ${u.numero}${u.complemento ? `, ${u.complemento}` : ''} — ${u.bairro}, ${u.cidade}/${u.uf}`,
              ],
              ['Cadastro', dataHora(u.createdAt)],
              [
                'Último acesso',
                u.ultimoAcessoEm ? dataHora(u.ultimoAcessoEm) : 'Nunca entrou',
              ],
              [
                'E-mail verificado',
                u.emailVerificadoEm ? dataHora(u.emailVerificadoEm) : 'Não',
              ],
              ...(u.asaasCustomerId
                ? ([['Cliente no Asaas', u.asaasCustomerId]] as const)
                : []),
            ]}
          />
        </Cartao>

        <Cartao titulo="Saldo">
          <div className={css.saldos}>
            <div>
              <span className={css.saldoRotulo}>Disponível</span>
              <span className={`${css.saldoValor} num`}>
                {reais(u.carteira?.saldoCentavos ?? 0)}
              </span>
            </div>
            <div>
              <span className={css.saldoRotulo}>Reservado</span>
              <span
                className={`${css.saldoValor} ${(u.carteira?.reservadoCentavos ?? 0) > 0 ? css.saldoReservado : ''} num`}
              >
                {reais(u.carteira?.reservadoCentavos ?? 0)}
              </span>
            </div>
          </div>

          <form onSubmit={creditar} className={css.formCredito}>
            <Campo
              rotulo="Lançar crédito"
              inputMode="numeric"
              value={
                valorCredito
                  ? (Number(valorCredito.replace(/\D/g, '')) / 100)
                      .toFixed(2)
                      .replace('.', ',')
                  : ''
              }
              onChange={(e) => setValorCredito(e.target.value)}
              placeholder="0,00"
              dica="Em reais. O motivo fica registrado no razão do cliente."
            />
            <Campo
              rotulo="Motivo"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex.: Pix recebido fora do sistema"
              maxLength={200}
            />
            <div>
              <Botao
                type="submit"
                pequeno
                carregando={creditando}
                disabled={!valorCredito || !motivo.trim()}
              >
                Creditar
              </Botao>
            </div>
          </form>
        </Cartao>
      </Colunas>

      <Cartao
        titulo="Ações"
        descricao="Toda ação fica registrada na auditoria com o seu e-mail."
      >
        <div className={css.acoes}>
          {u.ativo ? (
            <Botao
              aparencia="perigo"
              pequeno
              icone={<Lock />}
              onClick={() =>
                void alterar(
                  { ativo: false },
                  {
                    titulo: 'Bloquear esta conta?',
                    mensagem: `Bloquear ${u.email}? As sessões abertas caem na hora, as chaves de API param de autenticar e a pessoa não consegue entrar.`,
                    confirmar: 'Bloquear conta',
                    perigo: true,
                  },
                )
              }
            >
              Bloquear conta
            </Botao>
          ) : (
            <Botao
              aparencia="secundario"
              pequeno
              icone={<LockOpen />}
              onClick={() => void alterar({ ativo: true })}
            >
              Desbloquear conta
            </Botao>
          )}

          <Botao
            aparencia="secundario"
            pequeno
            icone={<KeyRound />}
            carregando={resetando}
            onClick={() => void resetarSenha(u.email)}
          >
            Redefinir senha
          </Botao>

          {!u.emailVerificadoEm && (
            <Botao
              aparencia="secundario"
              pequeno
              icone={<BadgeCheck />}
              onClick={() =>
                void alterar(
                  { verificarEmail: true },
                  {
                    titulo: 'Verificar o e-mail manualmente?',
                    mensagem: `Marcar o e-mail de ${u.email} como verificado sem o código?`,
                    confirmar: 'Marcar como verificado',
                  },
                )
              }
            >
              Verificar e-mail manualmente
            </Botao>
          )}

          {u.role === 'USER' ? (
            <Botao
              aparencia="secundario"
              pequeno
              icone={<ShieldCheck />}
              onClick={() =>
                void alterar(
                  { role: 'ADMIN' },
                  {
                    titulo: 'Promover a administrador?',
                    mensagem: `Promover ${u.email} a admin? A conta passa a ver o painel administrativo e o saldo de todos.`,
                    confirmar: 'Promover a admin',
                    perigo: true,
                  },
                )
              }
            >
              Promover a admin
            </Botao>
          ) : (
            <Botao
              aparencia="secundario"
              pequeno
              icone={<ShieldMinus />}
              onClick={() =>
                void alterar(
                  { role: 'USER' },
                  {
                    titulo: 'Rebaixar para usuário comum?',
                    mensagem: `Rebaixar ${u.email} para usuário comum? A conta perde o acesso ao painel administrativo.`,
                    confirmar: 'Rebaixar',
                  },
                )
              }
            >
              Rebaixar para usuário
            </Botao>
          )}
        </div>
      </Cartao>

      <Colunas minimo={360} alinhar="start">
        <Lista
          titulo="Extrato"
          vazio="Nenhum movimento."
          itens={ficha.lancamentos.map((l) => ({
            id: l.id,
            principal: `${legivel(l.tipo)} · ${reais(l.valorCentavos)}`,
            secundario: l.descricao,
            direita: dataHora(l.createdAt),
          }))}
        />
        <Lista
          titulo="Recargas"
          vazio="Nenhuma recarga."
          itens={ficha.recargas.map((r) => ({
            id: r.id,
            principal: `${reais(r.valorCentavos)} · ${legivel(r.status)}`,
            secundario: r.asaasPaymentId ?? '—',
            direita: dataHora(r.pagaEm ?? r.createdAt),
          }))}
        />
        <Lista
          titulo="Buscas"
          vazio="Nenhuma busca."
          itens={ficha.lotes.map((j) => ({
            id: j.id,
            principal: `${plural(j.total, 'chave')} · ${legivel(j.status)}`,
            secundario: `${plural(j.ok, 'baixado')}, ${plural(j.notfound, 'não achada', 'não achadas')}`,
            direita: dataHora(j.createdAt),
          }))}
        />
        <Lista
          titulo="Análises SPED"
          vazio="Nenhuma análise."
          itens={ficha.analises.map((a) => ({
            id: a.id,
            principal: a.nome,
            secundario: `${plural(a.totalNotas, 'nota')} · ${legivel(a.status)}`,
            direita: dataHora(a.createdAt),
          }))}
        />
        <Lista
          titulo="Chaves de API"
          vazio="Nenhuma chave emitida."
          itens={ficha.apiKeys.map((k) => ({
            id: k.id,
            principal: `${k.nome}${k.revogadaEm ? ' · revogada' : ''}`,
            secundario: k.ultimoUso
              ? `Último uso ${dataHora(k.ultimoUso)}`
              : 'Nunca usada',
            direita: dataHora(k.createdAt),
          }))}
        />
        <Lista
          titulo="Acessos"
          vazio="Nenhum acesso registrado."
          itens={ficha.acessos.map((a) => ({
            id: a.id,
            principal: a.sucesso ? 'Entrou' : `Recusado · ${a.motivo ?? '—'}`,
            secundario: `${a.ip ?? 'IP desconhecido'} · ${a.userAgent ?? '—'}`,
            direita: dataHora(a.createdAt),
          }))}
        />
        <Lista
          titulo="Ações do suporte nesta conta"
          vazio="Nenhuma ação administrativa."
          itens={ficha.auditoria.map((l) => ({
            id: l.id,
            principal: l.acao,
            secundario: `${l.adminEmail}${l.detalhe ? ` — ${l.detalhe}` : ''}`,
            direita: dataHora(l.createdAt),
          }))}
        />
      </Colunas>
    </>
  );
}

/* ── Peças ──────────────────────────────────────────────────────────────── */

function Lista({
  titulo,
  itens,
  vazio,
}: {
  titulo: string;
  vazio: string;
  itens: Array<{
    id: string;
    principal: string;
    secundario: string;
    direita: string;
  }>;
}) {
  return (
    <Cartao padding={0} titulo={titulo}>
      {itens.length === 0 ? (
        <p className={css.listaVazia}>{vazio}</p>
      ) : (
        <ul className={css.lista}>
          {itens.map((i) => (
            <li key={i.id} className={css.listaItem}>
              <div className={css.listaTexto}>
                <span className={css.listaPrincipal}>{i.principal}</span>
                <span className={css.listaSecundario} title={i.secundario}>
                  {i.secundario}
                </span>
              </div>
              <span className={`${css.listaData} num`}>{i.direita}</span>
            </li>
          ))}
        </ul>
      )}
    </Cartao>
  );
}
